import GamedayMiniAppContainer from './containers/GamedayMiniAppContainer';
import datasourceManager from 'datasource-manager';
import DeviceInteracton from 'device-interaction';
import customUrlBuilder, { customHashUrlBuilder } from './store/customUrlBuilder';
import HashState from './store/HashState';
import { resourcePath } from '@gameday/utils/resource-path';
import { getFormattedLangOptions } from '@gameday/utils/languageHelpers';

class GamedayMiniAppBase {
    constructor (_opts) {
        this.appContainer = GamedayMiniAppContainer;
        const mergedOptions = {
            route: '/',
            tz: 'America/New_York',
            mount: '#bdata-react-component',
            ..._opts
        };

        const {
            mount,
            language,
            locale,
            game,
            timecode,
            config,
            tracking,
            detail,
            game_state,
            game_tab,
            basename,
            ads,
            useMemoryHistory
        } = mergedOptions;

        const isPlay = !!(game_state === 'play');
        const { updatedLanguage, updatedLocale } = getFormattedLangOptions(language, locale);
        const {
            env = 'prod',
            enableTabsFinal = {},
            enableTabsLive = {},
            enableSummaryFiltersLive = {},
            enableSummaryFiltersFinal = {},
            appContext = undefined, // Explicitly undefined by default so the context can be malliable.
            resourcePathsOverride = undefined,
            componentId,
            useFullViewport = true,
            dataRouter,
            isStickyScore,
            teamId,
            themeMode = 'auto',
            hashState = {},
            absConfig = {},
            timezone = 'America/New_York',
            appPath = '',
            ...restConfig
        } = config;

        const initHashState = {
            read: false,
            write: false,
            watch: false,
            separator: ',',
            ...hashState
        };

        if (initHashState.watch) {
            this.hashListener = new HashState({ game, timecode });
        }

        this.opts = {
            ..._opts,
            mount,
            routes: dataRouter,
            basename,
            customUrlBuilder: initHashState.write ? customHashUrlBuilder : customUrlBuilder,
            useMemoryHistory: useMemoryHistory ?? initHashState.read,
            locale: updatedLocale,
            language: updatedLanguage,
            initState: {
                config: {
                    absConfig: {
                        chip: false,
                        pitch: true,
                        ...absConfig
                    },
                    appPath: appPath !== '' ? `/${appPath}` : '',
                    configGamePk: game,
                    env,
                    appContext,
                    hashState: initHashState,
                    /*  START: Centralize the fallback values we use for context inspection to the store.
                            These are to be used only if the stitch config call fails, and we need to choose an offline default.
                    */
                    failsafeAppContext: 'mlb',
                    failsafeGameContext: 'mlb',
                    failsafeDisablePlayerLinks: true,
                    failsafeHeadshotPath: '',
                    failsafeHeadshotType: 'spots',
                    /*  END: Centralize the fallback values we use for context inspection to the store */
                    summaryParallaxAdSlotIndex: 2,
                    enableTabsFinal: {
                        wrap: true,
                        summary: true,
                        box: true,
                        video: true,
                        ...enableTabsFinal
                    },
                    enableTabsLive: {
                        live: true,
                        summary: true,
                        box: true,
                        video: true,
                        ...enableTabsLive
                    },
                    enableSummaryFiltersLive: {
                        all: true,
                        keyMoments: true,
                        scoringPlays: true,
                        highlights: true,
                        homeRuns: true,
                        strikeouts: true,
                        hardHits: true,
                        highLeverage: false,
                        leadChange: true,
                        winProb: true,
                        ...enableSummaryFiltersLive
                    },
                    enableSummaryFiltersFinal: {
                        keyMoments: true,
                        scoringPlays: true,
                        all: true,
                        highlights: true,
                        homeRuns: true,
                        strikeouts: true,
                        hardHits: true,
                        highLeverage: false,
                        leadChange: true,
                        winProb: true,
                        ...enableSummaryFiltersFinal
                    },
                    tracking,
                    ads,
                    deviceInteraction: DeviceInteracton.create(config?.deviceInteractionConfig || config?.appView || 'desktop'),
                    enableFieldVision: false,
                    enableFieldVisionOD: false,
                    forceFieldVisionOn: false,
                    enableDarkMode: false,
                    themeMode,
                    batEnabled: true,
                    disableWebSockets: true,
                    webSocketWindow: 18,
                    forceWebSockets: false,
                    noDelayWebSockets: false,
                    timezone,
                    nativeVideoPlayer: false,
                    disableDynamicFieldView: false,
                    disableDynamicFieldViewDesktop: false,
                    disableDynamicFieldViewMobile: false,
                    DFVSyncThresholdAhead: 0,
                    DFVSyncThresholdBehind: 2,
                    forceDFV: false,
                    enablePOTG: false,
                    disableTicketLink: false,
                    isStickyScore: (!isPlay) ? isStickyScore : '',
                    teamId,
                    useFullViewport,
                    dfvPlay: {
                        disabled: false,
                        pitchTiming: 8.5,
                        resultTiming: 12.5
                    },
                    locale: updatedLocale,
                    language: updatedLanguage,
                    blockLoginActivities: updatedLanguage === 'ja',
                    ...restConfig
                },
                views: {
                    locale: updatedLocale,
                    language: updatedLanguage,
                    gamePk: game,
                    env,
                    timecode,
                    detail,
                    game_view: (!isPlay) ? componentId || game_state : '',
                    game_tab: (!isPlay) ? game_tab : '',
                    isPlay,
                    atBatIndex: (isPlay) ? game_tab : undefined
                }
            }
        };

        datasourceManager.setEnv(env);
        if (resourcePathsOverride) {
            resourcePath.set(resourcePathsOverride);
        }
    }

    init (_root) {
        this.root = _root.app;
        this.store = _root.store;
        this.render = _root.render;
        this.prefetch = _root.prefetch;
        this.renderWithStyle = _root.renderWithStyle;
    }
};

export default GamedayMiniAppBase;
